import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Container, Fab } from "@mui/material";
import type { JSX } from "react";
import React, { useCallback, useEffect, useState } from "react";

import { validateUserLogin } from "../auth/ValidateUserLogin";
import FullScreenMenu from "../Elements/FullScreenMenu";
import LoadingBackdrop from "../Elements/LoadingBackdrop";
import { ScrollTop } from "../Elements/ScrollTop";
import { waitForTaskToFinish } from "../utils/BackendClient";

import NavMenu from "./NavMenu";

type AlertLevel = "success" | "error";

export type LayoutAlert = {
  message: string;
  level: AlertLevel;
  onCloseAlert: () => void;
};

type LayoutState = {
  isReindexing: boolean;
  userIsAuthenticated: boolean;
  alert: LayoutAlert | null;
  fullScreenMenuIsOpen: boolean;
  reindexSuccessful: boolean | undefined;
};

export type LayoutProps = React.PropsWithChildren<Record<string, unknown>>;

export default function Layout(props: LayoutProps): JSX.Element {
  const [state, setState] = useState<LayoutState>({
    isReindexing: true,
    userIsAuthenticated: false,
    alert: null,
    fullScreenMenuIsOpen: false,
    reindexSuccessful: undefined,
  });

  const onCloseReindexingAlert = useCallback((): void => {
    setState((prev) => ({ ...prev, alert: null, isReindexing: false }));
  }, []);

  const toggleMenu = useCallback((): void => {
    setState((prev) => ({
      ...prev,
      fullScreenMenuIsOpen: !prev.fullScreenMenuIsOpen,
    }));
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function checkBackgroundTask(): Promise<void> {
      // IMPORTANT: don't pass captured `state` into the async helper.
      // If your helper requires a state snapshot, pass a safe minimal object or refactor it.
      const task = await waitForTaskToFinish("background-tasks/reindexing", setState);

      const alert: LayoutAlert | null = !task
        ? null
        : task.lastRunSuccessful
          ? {
              message: "Reindex successful",
              level: "success",
              onCloseAlert: onCloseReindexingAlert,
            }
          : {
              message: "Reindex failed",
              level: "error",
              onCloseAlert: onCloseReindexingAlert,
            };

      const userIsAuthenticated = await validateUserLogin();

      if (cancelled) {
        return;
      }

      setState((prev) => ({
        ...prev,
        userIsAuthenticated,
        reindexSuccessful: task?.lastRunSuccessful,
        isReindexing: Boolean(task),
        alert,
      }));
    }

    void checkBackgroundTask();

    return () => {
      cancelled = true;
    };
  }, [onCloseReindexingAlert]);

  if (state.isReindexing) {
    return <LoadingBackdrop alert={state.alert ?? undefined} />;
  }

  return (
    <div>
      <FullScreenMenu open={state.fullScreenMenuIsOpen} onMenuClose={toggleMenu} />
      <NavMenu userIsAuthenticated={state.userIsAuthenticated} onMenuClick={toggleMenu} />
      <div id="back-to-top-anchor" />
      <Container>{props.children}</Container>
      <ScrollTop {...props}>
        <Fab color="secondary" size="small">
          <KeyboardArrowUpIcon />
        </Fab>
      </ScrollTop>
    </div>
  );
}
